import documentsService from "../services/documents";
import workflowService from "../services/workflow";
import shortcutsService from "../services/shortcutsService";

/**
 * ============================================================================
 * Document Templates Archive API
 * Manages archived form templates
 * ============================================================================
 */

/**
 * Get archived forms with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Results per page (default: 10)
 * @param {number} params.offset - Skip N results (default: 0)
 * @returns {Promise} API response with { items: [], total: 0 }
 */
export function getArchivedFormsApi(params = {}) {
  return documentsService({
    url: "/forms/archived",
    method: "get",
    params
  });
}

/**
 * Restore an archived form
 * @param {string} id - Form ID to restore
 * @returns {Promise} API response with restored form
 */
export function restoreFormApi(id) {
  return documentsService({
    url: `/forms/${id}/restore`,
    method: "put"
  });
}

/**
 * Permanently delete an archived form
 * @param {string} id - Form ID to delete
 * @returns {Promise} API response
 */
export function deleteFormPermanentlyApi(id) {
  return documentsService({
    url: `/forms/${id}`,
    method: "delete"
  });
}

/**
 * ============================================================================
 * Workflows Archive API
 * Manages archived workflows
 * ============================================================================
 */

/**
 * Get archived workflows with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Results per page (default: 10)
 * @returns {Promise} API response with { workflows: [], total: 0 }
 */
export function getArchivedWorkflowsApi(params = {}) {
  return workflowService({
    url: "/workflows/archived",
    method: "get",
    params
  });
}

/**
 * Restore an archived workflow
 * @param {string} id - Workflow ID to restore
 * @returns {Promise} API response with restored workflow
 */
export function restoreWorkflowApi(id) {
  return workflowService({
    url: `/workflows/${id}/restore`,
    method: "put"
  });
}

/**
 * Permanently delete an archived workflow
 * @param {string} id - Workflow ID to delete
 * @returns {Promise} API response
 */
export function deleteWorkflowPermanentlyApi(id) {
  return workflowService({
    url: `/workflows/${id}`,
    method: "delete"
  });
}

/**
 * Get archived programs with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Results per page (default: 10)
 * @returns {Promise} API response with { workflows: [], total: 0 }
 */
export function getArchivedProgramsApi(params = {}) {
  return workflowService({
    url: "/workflows/archived",
    method: "get",
    params: {
      ...params,
      is_program: 'true'
    }
  });
}

/**
 * Restore an archived program
 * @param {string} id - Program ID to restore
 * @returns {Promise} API response with restored program
 */
export function restoreProgramApi(id) {
  return workflowService({
    url: `/workflows/${id}/restore`,
    method: "put"
  });
}

/**
 * Permanently delete an archived program
 * @param {string} id - Program ID to delete
 * @returns {Promise} API response
 */
export function deleteProgramPermanentlyApi(id) {
  return workflowService({
    url: `/workflows/${id}`,
    method: "delete"
  });
}

/**
 * ============================================================================
 * Shortcuts (Product Bundle) Archive API
 * Manages archived shortcuts/product bundles
 * Base URL: https://www.vibrant-america.com/lisapi/v1/pricing/item/catalog
 * ============================================================================
 */

/**
 * Get archived shortcuts with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.customer_id - Customer (provider) ID (required)
 * @param {number} params.clinic_id - Clinic ID (optional, for practice-level shortcuts)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Page size (default: 20, 1-100)
 * @returns {Promise} API response with { shortcuts: [], total: 0, page: 1, limit: 10, total_pages: 1 }
 */
export function getArchivedShortcutsApi(params = {}) {
  return shortcutsService({
    url: "/item/catalog/shortcut/archived",
    method: "get",
    params
  });
}

/**
 * Unarchive (restore) a shortcut
 * Sets is_deleted to NULL, making the shortcut active again
 * @param {number} shortcutId - Shortcut ID to unarchive
 * @returns {Promise} API response "success" on success
 */
export function unarchiveShortcutApi(shortcutId) {
  return shortcutsService({
    url: "/item/catalog/shortcut/unarchive",
    method: "put",
    params: { shortcut_id: shortcutId }
  });
}

/**
 * Hide an archived shortcut permanently
 * Sets is_deleted to false, permanently hiding the shortcut
 * @param {number} shortcutId - Shortcut ID to hide
 * @returns {Promise} API response null on success
 */
export function hideShortcutApi(shortcutId) {
  return shortcutsService({
    url: "/item/catalog/shortcut/hide",
    method: "delete",
    params: { shortcut_id: shortcutId }
  });
}

/**
 * ============================================================================
 * Merchandise (Practice Products) Archive API
 * Manages archived merchandise (practice products)
 * Base URL: https://www.vibrant-america.com/lisapi/v1/pricing/merchandise/
 * ============================================================================
 */

/**
 * Get archived merchandise with pagination
 * @param {Object} params - Query parameters
 * @param {number} params.customer_id - Customer (provider) ID (required)
 * @param {number} params.clinic_id - Clinic ID (required)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Page size (default: 20, 1-100)
 * @returns {Promise} API response with { merchandise: [], total: 0, page: 1, limit: 10, total_pages: 1 }
 */
export function getArchivedMerchandisesApi(params = {}) {
  return shortcutsService({
    url: "/merchandise/getArchivedMerchandises",
    method: "get",
    params
  });
}

/**
 * Restore an archived merchandise
 * Restores an archived merchandise back to active status by clearing is_deleted (setting it to NULL)
 * @param {number} merchandiseId - Merchandise ID to restore
 * @returns {Promise} API response null on success
 */
export function restoreMerchandiseApi(merchandiseId) {
  return shortcutsService({
    url: "/merchandise/restoreMerchandise",
    method: "put",
    params: { merchandise_id: merchandiseId }
  });
}

/**
 * Hide an archived merchandise permanently
 * Permanently hides an archived merchandise by setting is_deleted = false. This is irreversible.
 * Only merchandise that are already archived (is_deleted = true) can be hidden.
 * @param {number} merchandiseId - Merchandise ID to hide
 * @returns {Promise} API response null on success
 */
export function hideMerchandiseApi(merchandiseId) {
  return shortcutsService({
    url: "/merchandise/hideMerchandise",
    method: "delete",
    params: { merchandise_id: merchandiseId }
  });
}

/**
 * Archive a merchandise
 * Archives a merchandise by setting is_deleted = true
 * @param {number} merchandiseId - Merchandise ID to archive
 * @returns {Promise} API response null on success
 */
export function archiveMerchandiseApi(merchandiseId) {
  return shortcutsService({
    url: "/merchandise/deleteMerchandise",
    method: "delete",
    params: { merchandise_id: merchandiseId }
  });
}
